import React, { useEffect, useState } from "react";
import classNames from "classnames";
import dayjs from "dayjs";
import Badge from "../../Components/Badge";
import { useTranslation } from "react-i18next";
import PointIcon from "../../Components/PointIcon";
import {
  requestGetPointBalance,
  requestListPointTransaction,
  requestListRewardsProduct,
  useAuthState,
  usePointDispatch,
  usePointState,
} from "../../Context";
import { ErrorList } from "../../Components/ErrorList";
import { EmptyList } from "../../Components/EmptyList";
import TableSkeleton from "../../Components/TableSkeleton";
import { Pagination } from "../../Components/Pagination";
import { useNavigate } from "react-router-dom";
import ProductListSkeleton from "../../Components/ProductListSkeleton";
import RewardProduct from "../../Components/RewardProduct";
import { ViewListIcon } from "@heroicons/react/outline";

const PointsDisplay = ({ points = 0, loading = false }) => {
  const { t } = useTranslation();

  return (
    <div className="bg-gradient-to-r from-blue-400 to-blue-600 text-white font-bold rounded-md p-4 w-full md:w-1/2">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <PointIcon className="mr-2 shrink-0" />
          <span>{t("totalPoints")}</span>
        </div>
        {loading ? (
          <div className="flex animate-pulse items-center rounded-lg shrink-0">
            <div className="my-1 h-3 w-20 rounded-md bg-gray-300"></div>
          </div>
        ) : (
          <span>{points.toLocaleString("id-ID")}</span>
        )}
      </div>
    </div>
  );
};

const TransactionRecord = ({
  transactions,
  loading,
  error,
  nextTokens,
  pointDispatch,
  user,
}) => {
  const { t } = useTranslation();

  const [page, setpage] = useState(1);

  const onNext = () => {
    const nextPage = page + 1;
    setpage(nextPage);
    if (nextTokens[nextPage]) {
      requestListPointTransaction(pointDispatch, {
        page: nextPage,
        owner: user.id,
        nextToken: nextTokens[nextPage],
      });
    }
  };

  const onPrev = () => {
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0) {
      requestListPointTransaction(pointDispatch, {
        page: prevPage,
        owner: user.id,
        nextToken: nextTokens[prevPage],
      });
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (nextTokens[selectedPage] || selectedPage === 1) {
      requestListPointTransaction(pointDispatch, {
        page: selectedPage,
        owner: user.id,
        nextToken: nextTokens[selectedPage],
      });
    }
  };

  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
          <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                    {t("Date")}
                  </th>
                  <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                    {t("Description")}
                  </th>
                  <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                    {t("Status")}
                  </th>
                  <th className="px-4 py-2 font-medium text-sm text-right text-gray-900 whitespace-nowrap">
                    {t("point")}
                  </th>
                </tr>
              </thead>
              {loading ? (
                <TableSkeleton rows={6} columns={4} />
              ) : (
                <tbody className="divide-y divide-gray-200 bg-white">
                  {transactions.length > 0 ? (
                    transactions.map((transaction, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 text-sm text-left text-gray-700">
                          {dayjs(transaction.createdAt).format("DD MMM YYYY")}
                        </td>
                        <td className="px-4 py-2 text-sm text-left text-gray-700">
                          {transaction.description}
                        </td>
                        <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                          {transaction.status === "PENDING" ? (
                            <Badge title="Pending" type="alert" />
                          ) : null}
                          {transaction.status === "SUCCESS" ? (
                            <Badge title="Success" type="success" />
                          ) : null}
                          {transaction.status === "FAILED" ? (
                            <Badge title="Failed" type="danger" />
                          ) : null}
                          {transaction.status === "EXPIRED" ? (
                            <Badge title="Expired" type="secondary" />
                          ) : null}
                          {transaction.status === "REFUNDED" ? (
                            <Badge title="Refunded" type="secondary" />
                          ) : null}
                        </td>
                        <td
                          className={classNames(
                            "px-4 py-2 text-sm text-right",
                            {
                              "text-red-600": transaction.total < 0,
                              "text-green-600": transaction.total > 0,
                              "text-gray-600": transaction.total === 0,
                            }
                          )}
                        >
                          {transaction.total > 0 && "+"}
                          {transaction.total?.toLocaleString("id-ID")}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="p-4" colSpan={4}>
                        {error ? (
                          <ErrorList
                            message={
                              error?.message || "Error fetching transactions."
                            }
                            errors={error?.errors}
                          />
                        ) : (
                          <EmptyList title="noPointTransaction" />
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              )}
            </table>
            <Pagination
              page={page}
              onPrev={onPrev}
              onNext={onNext}
              onPageChange={onPageChange}
              nextTokens={nextTokens}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductList = ({ products, productLoading, productError }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div>
      <div className="bg-white shadow-sm max-w-7xl mx-auto overflow-hidden px-4 py-6 sm:px-6 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <div className="text-base font-medium text-gray-900">
            {t("redeemYourPoints")}
          </div>
          <button
            disabled={productLoading || productError || !products?.length}
            onClick={() => navigate("/rewards-product")}
            className="text-sm text-blue-600 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            {t("See All")}
          </button>
        </div>
        {productLoading ? (
          <ProductListSkeleton totalItem={4} />
        ) : (
          <div className="flex flex-col gap-4">
            {products?.length > 0 ? (
              <div className="-mx-px grid grid-cols-1 sm:grid-cols-2 sm:mx-0 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {products.map((product) => (
                  <RewardProduct product={product} key={product?.id} />
                ))}
              </div>
            ) : productError ? (
              <ErrorList
                message={productError?.message || "Error fetching product."}
                errors={productError?.errors}
              />
            ) : (
              <EmptyList title="noRewardProducts" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const RedemptionHistoryCard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <button
      onClick={() => navigate("/redemption-history")}
      className="bg-white border border-blue-600 text-gray-900 font-bold rounded-md p-4 w-full md:w-1/2"
    >
      <div className="flex items-center">
        <ViewListIcon className="w-6 h-6 mr-2" />
        <span>{t("pointsRedemptionHistory")}</span>
      </div>
    </button>
  );
};

const PointTransactionLayout = ({
  points,
  pointsLoading,
  transactions,
  pointTransactionsLoading,
  pointTransactionsError,
  pointDispatch,
  nextTokens,
  user,
  products,
  productLoading,
  productError,
  productNextTokens,
}) => {
  return (
    <div className="mx-auto flex flex-col gap-6">
      <div className="flex gap-6 justify-between sm:flex-row flex-col">
        <PointsDisplay points={points} loading={pointsLoading} />
        <RedemptionHistoryCard />
      </div>
      <ProductList
        products={products}
        productLoading={productLoading}
        productError={productError}
        productNextTokens={productNextTokens}
        pointDispatch={pointDispatch}
      />
      <TransactionRecord
        transactions={transactions}
        loading={pointTransactionsLoading}
        error={pointTransactionsError}
        nextTokens={nextTokens}
        pointDispatch={pointDispatch}
        user={user}
      />
    </div>
  );
};

export const RewardPoints = () => {
  const pointDispatch = usePointDispatch();
  const { user } = useAuthState();
  const {
    loading,
    balance,
    pointTransactions,
    pointTransactionsLoading,
    pointTransactionsError,
    nextTokens,
    products,
    productLoading,
    productError,
    productNextTokens,
  } = usePointState();

  useEffect(() => {
    if (user.id) {
      requestListPointTransaction(pointDispatch, {
        owner: user.id,
        page: 1,
      });
    }
  }, [pointDispatch, user.id]);

  useEffect(() => {
    requestListRewardsProduct(pointDispatch, {
      page: 1,
      limit: 4,
    });
  }, [pointDispatch]);

  useEffect(() => {
    requestGetPointBalance(pointDispatch);
  }, [pointDispatch]);

  return (
    <PointTransactionLayout
      points={balance.balance}
      pointsLoading={loading}
      transactions={pointTransactions}
      pointTransactionsLoading={pointTransactionsLoading}
      pointTransactionsError={pointTransactionsError}
      pointDispatch={pointDispatch}
      nextTokens={nextTokens}
      user={user}
      products={products}
      productLoading={productLoading}
      productError={productError}
      productNextTokens={productNextTokens}
    />
  );
};
